import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { NavbarActionsProvider } from '../../context/NavbarActionsContext';
import TopLoadingBar from '../TopLoadingBar';
import { useUserSettingsContext } from '../../context/UserSettingsContext';
import { useStreamChat } from '../../context/StreamChatContext';
import StreamChatBox from '../Chat/StreamChatBox';
import NotificationManager from '../Notifications/NotificationManager';
import MfaGuardGate from '../MfaGuardGate';
import { SidebarInset, SidebarProvider, useSidebar } from '../ui/sidebar';
import { useResolvedNav } from '../../hooks/useResolvedNav';
import AppSidebar from './shell/AppSidebar';
import AppHeader from './shell/AppHeader';
import CommandPalette from './shell/CommandPalette';
import { cn } from '../../lib/utils';
import { consumeShellEntrance } from '../../utils/uiPreferences';
import { MORPH_ATTR, REVEALED_EVENT } from '../NewLookPrompt/newLookTransition';
import NewLookTour from '../NewLookTour/NewLookTour';

// Routes that own their full height/width (their own toolbars and scroll
// regions) and so skip the content padding.
const FULL_BLEED_PREFIXES = ['/forecast-v3', '/dashboard/night-audit', '/chat'];
const isFullBleed = (pathname) =>
  FULL_BLEED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Publishes the content card's horizontal geometry as CSS vars so fixed,
 * viewport-centred UI (report filter dock, dashboard float bar) centres on
 * the card instead of the whole window. Card left edge: 16rem sidebar when
 * open; 3rem icon rail + 1rem padding + 2px + 0.5rem card margin when collapsed.
 */
function ShellGeometry() {
  const { state, isMobile } = useSidebar();
  useEffect(() => {
    const root = document.documentElement.style;
    const left = isMobile ? 0 : state === 'collapsed' ? 74 : 256;
    const right = isMobile ? 0 : 8;
    root.setProperty('--shell-content-left', `${left}px`);
    root.setProperty('--shell-content-right', `${right}px`);
    root.setProperty('--shell-center-shift', `${(left - right) / 2}px`);
    return () => {
      root.removeProperty('--shell-content-left');
      root.removeProperty('--shell-content-right');
      root.removeProperty('--shell-center-shift');
    };
  }, [state, isMobile]);
  return null;
}

/**
 * Desktop app shell ("New" layout) — shadcn "sidebar-inset" layout: a black collapsible
 * sidebar and the page on a white inset card with a sticky header.
 *
 * The DOCUMENT scrolls (not the card) because many pages track window
 * scroll; fixed masks over the card's top/bottom gaps keep it looking pinned.
 * Page-level sticky bars offset by `var(--shell-sticky-top)`.
 */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Expands the (initially collapsed) rail once its slide-in has landed. */
function ShellEntrance({ onDone }) {
  const { setOpen } = useSidebar();
  useEffect(() => {
    const timers = [];
    const start = (delay) => {
      timers.push(setTimeout(() => setOpen(true), delay));
      timers.push(setTimeout(onDone, delay + 540));
    };
    // Switching from Classic: a cover is still over the shell — slide out only
    // once it has lifted, never underneath it.
    const covered = document.documentElement.hasAttribute(MORPH_ATTR);
    const onRevealed = () => start(60);
    if (covered) window.addEventListener(REVEALED_EVENT, onRevealed, { once: true });
    else start(160);
    return () => {
      window.removeEventListener(REVEALED_EVENT, onRevealed);
      timers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

const InsetLayout = () => {
  const location = useLocation();
  const { userSettings } = useUserSettingsContext();
  const { initializeChat } = useStreamChat();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatInitialized, setChatInitialized] = useState(false);
  const { nav, trail, pages, pathname } = useResolvedNav();

  // Canvas behind the inset card is the sidebar black.
  useEffect(() => {
    // Both html and body: the rubber-band overscroll area paints the root
    // element's background, so it must match the sidebar too.
    document.documentElement.classList.add('app-shell-inset');
    document.body.classList.add('app-shell-inset');
    return () => {
      document.documentElement.classList.remove('app-shell-inset');
      document.body.classList.remove('app-shell-inset');
    };
  }, []);

  // Chat identity: Cognito id → username → email → Amplify local storage.
  const userId = useMemo(() => {
    if (userSettings) {
      if (userSettings.cognitoId) return userSettings.cognitoId;
      if (userSettings.cognitoUsername) return userSettings.cognitoUsername;
      if (userSettings.email) return userSettings.email;
    }
    try {
      const userPoolId = localStorage.getItem('amplify-signin-with-webui');
      if (userPoolId) {
        const userData = JSON.parse(localStorage.getItem(userPoolId));
        if (userData && userData.username) return userData.username;
      }
    } catch (_) { /* malformed storage */ }
    return null;
  }, [userSettings]);

  // Connect chat app-wide (unread badge, presence) as soon as settings load.
  // initializeChat takes the EMAIL — the token route maps it to the Stream id;
  // a Cognito id has no '@' and was silently rejected.
  const chatEmail = userSettings?.email || null;
  useEffect(() => {
    if (chatEmail && !chatInitialized) {
      initializeChat(chatEmail, userSettings);
      setChatInitialized(true);
    }
  }, [chatEmail, userSettings, initializeChat, chatInitialized]);

  // New page, new scroll: the document scrolls, so a page opened from a
  // scrolled dashboard would otherwise start mid-way. A shared link's ?sy=
  // (useScrollUrlSync) still wins.
  useLayoutEffect(() => {
    if (!new URLSearchParams(window.location.search).has('sy')) window.scrollTo(0, 0);
  }, [location.pathname]);

  const toggleChat = useCallback(() => setIsChatOpen((prev) => !prev), []);

  // Open the chat modal, optionally on a specific channel.
  const openChatModal = useCallback((channel = null) => {
    setIsChatOpen(true);
    if (channel) {
      sessionStorage.setItem('selectedChatChannel', JSON.stringify({ id: channel.id, timestamp: Date.now() }));
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('selectChatChannel', { detail: { channel } }));
      }, 100);
    }
  }, []);

  useEffect(() => {
    window.openChatModal = openChatModal;
    const handleOpenChatModal = (event) => {
      const { channel, action } = event.detail || {};
      if (action === 'open') openChatModal(channel);
    };
    window.addEventListener('openChatModal', handleOpenChatModal);
    return () => {
      window.openChatModal = null;
      window.removeEventListener('openChatModal', handleOpenChatModal);
    };
  }, [openChatModal]);

  const fullBleed = isFullBleed(pathname);

  // Post-sign-in entrance (Login sets the flag): the login page already drew
  // the collapsed rail, so the shell mounts collapsed and slides out to its
  // full width; the card content fades in. Skipped under reduced motion.
  const [entrance, setEntrance] = useState(() => consumeShellEntrance() && !prefersReducedMotion());

  return (
    <NavbarActionsProvider>
      <SidebarProvider initialOpen={entrance ? false : undefined} className={entrance ? 'app-shell-entrance' : undefined}>
        <ShellGeometry />
        {entrance && <ShellEntrance onDone={() => setEntrance(false)} />}
        <MfaGuardGate />
        <TopLoadingBar />

        <AppSidebar nav={nav} pathname={pathname} />

        <SidebarInset className="app-shell-card min-h-[calc(100svh-16px)]">
          <AppHeader trail={trail} onToggleChat={toggleChat} />
          <div className={cn('relative flex min-w-0 flex-1 flex-col', fullBleed ? 'app-shell-bleed' : 'app-shell-body pb-8 pt-4')}>
            <Outlet />
          </div>
        </SidebarInset>

        <div aria-hidden className="app-shell-mask-top left-0" />
        <div aria-hidden className="app-shell-mask-bottom left-0" />

        <CommandPalette nav={nav} pages={pages} />
        <NewLookTour />

        {userId && (
          <StreamChatBox isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} userId={userId} />
        )}

        <NotificationManager
          onNotificationClick={(notification) => {
            if (notification.type === 'chat-message') {
              openChatModal(notification.channel);
            }
          }}
        />
      </SidebarProvider>
    </NavbarActionsProvider>
  );
};

export default InsetLayout;
